"use client";

import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import * as React from "react";
import { cn } from "#app/utils/misc.tsx";

function TooltipProvider({
  delay = 0,
  ...props
}: TooltipPrimitive.Provider.Props) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delay={delay}
      {...props}
    />
  );
}

function Tooltip({ ...props }: TooltipPrimitive.Root.Props) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger({ ...props }: TooltipPrimitive.Trigger.Props) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  side = "top",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  children,
  showArrow = true,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<
    TooltipPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset"
  > & {
    showArrow?: boolean;
  }) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className="isolate z-50"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            "z-50 inline-flex w-fit max-w-xs origin-(--transform-origin) items-center gap-1.5 rounded-xl bg-foreground px-3 py-1.5 text-xs text-background has-data-[slot=kbd]:pr-1.5 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-lg data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            className,
          )}
          {...props}
        >
          {children}
          {showArrow ? (
            <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-[2px] bg-foreground fill-foreground data-[side=bottom]:top-1 data-[side=inline-end]:top-1/2! data-[side=inline-end]:-left-1 data-[side=inline-end]:translate-x-[1.5px] data-[side=inline-end]:-translate-y-1/2 data-[side=inline-start]:top-1/2! data-[side=inline-start]:-right-1 data-[side=inline-start]:translate-x-[-1.5px] data-[side=inline-start]:-translate-y-1/2 data-[side=left]:top-1/2! data-[side=left]:-right-1 data-[side=left]:translate-x-[-1.5px] data-[side=left]:-translate-y-1/2 data-[side=right]:top-1/2! data-[side=right]:-left-1 data-[side=right]:translate-x-[1.5px] data-[side=right]:-translate-y-1/2 data-[side=top]:-bottom-2.5" />
          ) : null}
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  );
}

/**
 * Local addition. Trigger + content in one, replacing the
 * `<TooltipProvider><Tooltip><TooltipTrigger asChild>` stack.
 *
 * A single element child becomes the trigger itself via `render`, so the
 * positioner anchors to it. Do **not** wrap the child in a `display: contents`
 * span: that generates no box, so the tooltip lands at the top-left of the
 * viewport.
 */
function SimpleTooltip({
  title,
  children,
  side,
  className,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  side?: React.ComponentProps<typeof TooltipContent>["side"];
  className?: string;
}) {
  if (!title) return <>{children}</>;

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          React.isValidElement(children) ? (
            (children as React.ReactElement)
          ) : (
            <span className={cn("inline-block", className)}>{children}</span>
          )
        }
      />
      <TooltipContent side={side}>{title}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Collapsed sidebar / icon-rail tooltips. Merging the trigger onto Remix
 * `<Link>` (or animating buttons) remounts on hover and makes the tip blink.
 * Use a stable wrapper, ignore popup hover, and skip zoom animations.
 */
function RailTooltip({
  title,
  children,
  side = "right",
  triggerClassName,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  side?: React.ComponentProps<typeof TooltipContent>["side"];
  /** Extra classes on the stable trigger wrapper (e.g. `w-full` for the logo cell). */
  triggerClassName?: string;
}) {
  if (!title) return <>{children}</>;

  return (
    <TooltipProvider delay={200}>
      <Tooltip>
        <TooltipTrigger
          render={
            <span className={cn("inline-flex max-w-full", triggerClassName)} />
          }
        >
          {children}
        </TooltipTrigger>
        <TooltipContent
          side={side}
          sideOffset={10}
          showArrow={false}
          className={cn(
            "pointer-events-none",
            "animate-none data-open:animate-none data-closed:animate-none",
            "data-[state=delayed-open]:animate-none",
          )}
        >
          {title}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export {
  SimpleTooltip,
  RailTooltip,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
};
